<template>
  <ComponentState
    :fetch-status="[
      getUnderlyingAssetIsinsStatus,
      getUnderlyingAssetIsinEndDatesStatus,
    ]"
  >
    <template #loading>
      <TableSkeleton :row-number="15" :column-number="10" />
    </template>

    <template #error>
      <Error @retry="getContractsChain" />
    </template>

    <div
      class="h-full flex flex-col justify-start items-stretch gap-2 grow p-2 overflow-y-hidden"
    >
      <Filter
        :symbol="symbol"
        :view-mode="viewMode"
        :end-date="endDate"
        :selected-isin="selectedIsin"
        :underlying-asset-isins="underlyingAssetIsins"
        :underlying-asset-isin-end-dates="underlyingAssetIsinEndDates"
        @update:view-mode="onViewModeUpdate"
        @update:end-date="onEndDateUpdate"
      />

      <SymbolInfo
        v-if="activeUnderlyingInstrument"
        :isin="isin"
        :symbol="activeUnderlyingInstrument"
      />

      <TableContainer
        v-if="contractsChain && contractsChain.strikePrices?.length"
        :key="underlyingInstrumentIsin"
        :isin="isin"
        :chain="contractsChain"
        :view-mode="viewMode"
      >
        <MainTable
          ref="mainTableRef"
          :data="contractsChain?.calls"
          :view-mode="viewMode"
          :sort-by="sort"
          @scroll="onMainTableScroll"
        />
        <CentralTable
          ref="centralTableRef"
          :data="contractsChain?.strikePrices"
          :view-mode="viewMode"
          :sort-by="sort"
          @scroll="onCentralTableScroll"
          @sort-add="onSortAdd"
          @sort-remove="onSortRemove"
        />
        <InverseTable
          ref="inverseTableRef"
          :data="contractsChain?.puts"
          :view-mode="viewMode"
          :sort-by="sort"
          @scroll="onInverseTableScroll"
        />
      </TableContainer>

      <EmptyList
        v-else-if="!endDate"
        :title="t('option.chainNotAvailable')"
        :body="t('option.chainNotAvailableDescription')"
        class="h-full flex flex-col justify-center items-center"
        wrapper-class="w-auto"
        title-class="text-center mt-4 mb-2"
      >
        <template #image>
          <Image src="empty_box" alt="not_found" class="w-28 h-max" />
        </template>
      </EmptyList>

      <EmptyList
        v-else
        :title="t('option.optionChainEmptyListTitle')"
        :body="t('option.optionChainEmptyListBody')"
        title-class="text-center mt-3 "
        wrapper-class="w-full flex items-center justify-center flex-col gap-1"
        class="h-[calc(100%-70px)] flex flex-col justify-center items-center rounded-md my-3"
      >
        <template #image>
          <Image src="empty_box" alt="not_found" class="w-28 h-max" />
        </template>
      </EmptyList>
    </div>
  </ComponentState>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';

import { Instrument } from '@libs/api-reference';
import { useIsFetching } from '@libs/use';
import { isOptionSymbol, SYMBOL_QUERY_KEYS } from '@core/symbol';
import {
  CONTRACT_CHAIN_SIDE,
  useContractsChain,
  useLiveGetOptionContract,
  useUnderlyingAssetIsinEndDates,
  useUnderlyingAssetIsins,
} from '@core/option';

import Error from '@/components/Error.vue';
import Image from '@/components/Image.vue';
import EmptyList from '@/components/EmptyList.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import ComponentState from '@/components/ComponentState.vue';

import Filter from './Filter.vue';
import MainTable from './MainTable.vue';
import SymbolInfo from './SymbolInfo.vue';
import InverseTable from './InverseTable.vue';
import CentralTable from './CentralTable.vue';
import TableContainer from './TableContainer.vue';

const props = defineProps<{
  isin: string;
  symbol: Instrument;
}>();

const { t, d } = useI18n();

type Item = { label: string; value: string };
export type TViewMode = {
  label?: string;
  value: keyof typeof CONTRACT_CHAIN_SIDE;
};

const sort = ref({ prop: 'strikePrice', order: 'default' });
const mainTableRef = ref<{ tableRef?: { containerRef: HTMLElement } }>();
const inverseTableRef = ref<{ tableRef?: { containerRef: HTMLElement } }>();
const centralTableRef = ref<{ tableRef?: { containerRef: HTMLElement } }>();

const endDate = ref<Item | undefined>(undefined);
const viewMode = ref<TViewMode>({
  value: 0,
  label: t('option.contractType.both'),
});

const isOption = computed(() =>
  isOptionSymbol(props.symbol.instrumentTypeCode as string)
);

const isFetchingSymbol = useIsFetching({
  queryKey: SYMBOL_QUERY_KEYS.symbol(
    computed(() => props.symbol.isin as string)
  ),
});

const { optionContract } = useLiveGetOptionContract({
  isin: computed(() => props.isin as string),
  immediate: computed(() => isOption.value && !isFetchingSymbol.value),
});

const { underlyingAssetIsins, getUnderlyingAssetIsinsStatus } =
  useUnderlyingAssetIsins();

const underlyingInstrumentIsin = computed(() =>
  isOption.value
    ? (optionContract.value?.underlyingAssetIsin as string)
    : (props.symbol.isin as string)
);

const selectedIsin = computed(() => {
  if (isOption.value) {
    const currentItem = underlyingAssetIsins.value?.findIndex(
      (item) => item.isin === optionContract.value?.underlyingAssetIsin
    );

    if (typeof currentItem === 'number' && currentItem > -1) {
      return {
        label: underlyingAssetIsins.value?.[currentItem]
          .instrumentName as string,
        value: optionContract.value?.underlyingAssetIsin as string,
      };
    }

    return undefined;
  } else {
    const currentItem = underlyingAssetIsins.value?.findIndex(
      (item) => item.isin === props.symbol.isin
    );

    if (typeof currentItem === 'number' && currentItem > -1) {
      return {
        label: underlyingAssetIsins.value?.[currentItem]
          .instrumentName as string,
        value: props.symbol.isin as string,
      };
    }

    return undefined;
  }
});

const activeUnderlyingInstrument = computed(() => {
  if (isOption.value) {
    const currentItem = underlyingAssetIsins.value?.findIndex(
      (item) => item.isin === optionContract.value?.underlyingAssetIsin
    );

    if (typeof currentItem === 'number' && currentItem > -1) {
      return underlyingAssetIsins.value?.[currentItem];
    }

    return undefined;
  }

  const currentItem = underlyingAssetIsins.value?.findIndex(
    (item) => item.isin === props.symbol?.isin
  );

  if (typeof currentItem === 'number' && currentItem > -1) {
    return underlyingAssetIsins.value?.[currentItem];
  }

  return undefined;
});

const {
  underlyingAssetIsinEndDates,
  getUnderlyingAssetIsinEndDatesStatus,
  isSuccessLoadingUnderlyingAssetIsinEndDates,
  isRevalidatingUnderlyingAssetIsinEndDates,
} = useUnderlyingAssetIsinEndDates({
  underlyingInstrumentIsin,
  immediate: computed(() => !!underlyingInstrumentIsin.value),
});

const { contractsChain, getContractsChain } = useContractsChain({
  contractType: computed(() => viewMode.value.value),
  endDate: computed(() => {
    return endDate.value?.value || '';
  }),
  underlyingAssetIsin: underlyingInstrumentIsin,
  immediate: computed(
    () =>
      (isSuccessLoadingUnderlyingAssetIsinEndDates.value ||
        isRevalidatingUnderlyingAssetIsinEndDates.value) &&
      !!underlyingInstrumentIsin.value &&
      !!endDate.value?.value
  ),
});

const calculateScrollPercentage = (el?: HTMLElement): number => {
  if (!el) return 0;

  const maxScrollLeft = el.scrollWidth - el.clientWidth;

  return maxScrollLeft > 0 ? el.scrollLeft / maxScrollLeft : 0;
};

function onMainTableScroll(event: { top: number; left: number }) {
  const scrollPercentage = calculateScrollPercentage(
    mainTableRef.value?.tableRef?.containerRef
  );

  if (inverseTableRef.value?.tableRef) {
    const maxScrollLeft =
      inverseTableRef.value.tableRef.containerRef.scrollWidth -
      inverseTableRef.value.tableRef.containerRef.clientWidth;

    inverseTableRef.value.tableRef.containerRef.scrollTo({
      top: event.top,
      left: 1 - scrollPercentage * maxScrollLeft,
      behavior: 'auto',
    });
  }

  centralTableRef.value?.tableRef?.containerRef?.scrollTo({
    top: event.top,
    behavior: 'auto',
  });
}

function onInverseTableScroll(event: { top: number; left: number }) {
  const scrollPercentage = calculateScrollPercentage(
    inverseTableRef.value?.tableRef?.containerRef
  );

  if (mainTableRef.value?.tableRef?.containerRef) {
    const maxScrollLeft =
      mainTableRef.value.tableRef.containerRef.scrollWidth -
      mainTableRef.value.tableRef.containerRef.clientWidth;

    mainTableRef.value.tableRef.containerRef.scrollTo({
      top: event.top,
      left: 1 - scrollPercentage * maxScrollLeft,
      behavior: 'auto',
    });
  }

  centralTableRef.value?.tableRef?.containerRef?.scrollTo({
    top: event.top,
    behavior: 'auto',
  });
}

function onCentralTableScroll(event: { top: number; left: number }) {
  mainTableRef.value?.tableRef?.containerRef?.scrollTo({
    top: event.top,
    behavior: 'auto',
  });

  inverseTableRef.value?.tableRef?.containerRef?.scrollTo({
    top: event.top,
    behavior: 'auto',
  });
}

function onViewModeUpdate(value: TViewMode) {
  viewMode.value = value;
}

function onEndDateUpdate(value: Item) {
  endDate.value = value;
}

function onSortAdd(value) {
  if (value?.order === 'default') {
    sort.value = { prop: 'strikePrice', order: 'ascending' };
  } else {
    sort.value = { prop: 'strikePrice', order: value.order };
  }
}

function onSortRemove(value) {
  if (value?.order === 'descending') {
    sort.value = { prop: 'strikePrice', order: 'default' };
  }
}

watch(
  () => ({
    optionContract,
    underlyingEndDates: underlyingAssetIsinEndDates,
    revalidate:
      isRevalidatingUnderlyingAssetIsinEndDates ||
      isSuccessLoadingUnderlyingAssetIsinEndDates,
    isin: props.symbol.isin,
  }),
  ({ underlyingEndDates }) => {
    if (!underlyingEndDates.value?.endDates.length) return;

    let currentItem = underlyingEndDates.value?.endDates?.[0];

    if (isOption.value && optionContract.value?.endDate) {
      const findedItem = underlyingEndDates.value?.endDates?.find(
        (item) => item === optionContract.value?.endDate
      );

      if (findedItem) {
        currentItem = findedItem;
      }
    }

    if (!currentItem) {
      endDate.value = undefined;

      return;
    }

    endDate.value = {
      value: currentItem,
      label: `${t('modules.portfolio.embeddedPutOption.dueDate')} ${d(
        currentItem
      )}`,
    };
  },
  { immediate: true, deep: true }
);
</script>
